import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';

// Logout/login is SPA navigation (no page reload), so every cached response
// from the previous account survives unless it is dropped explicitly. Query
// keys like ['my-access'] are not user-scoped: within staleTime the next
// account is served the previous account's data without a refetch (Jim, 8/4:
// Derek's login showing Omar's sites). The service worker's API cache keys by
// URL only, so it can replay another user's response after a network failure.
async function purgeUserScopedCaches() {
  try {
    await caches.delete('envirolink-api-v1');
  } catch {
    // cache storage unavailable (private mode / non-secure context) — ignore
  }
}

export function useAuth() {
  const { isAuthenticated, user, login, logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signIn = async (
    email: string,
    password: string,
    options?: { skipNavigation?: boolean }
  ) => {
    const response = await authService.login(email, password);

    // Store tokens first so subsequent API calls include Authorization header
    useAuthStore
      .getState()
      .updateTokens(response.access_token, response.refresh_token);

    // Now get user info (this requires the token)
    const userInfo = await authService.getCurrentUser();

    queryClient.clear();
    void purgeUserScopedCaches();

    login(userInfo, response.access_token, response.refresh_token);

    // Redirect to mode selection page (unless caller wants to handle it)
    if (!options?.skipNavigation) {
      navigate('/select-mode');
    }
  };

  const signInWithOIDC = () => {
    // Redirect to OIDC provider
    const redirectUri = `${window.location.origin}/auth/callback`;
    window.location.href = `/api/v1/auth/oidc/login?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  const signOut = async () => {
    try {
      await authService.logout();
    } catch {
      // Continue with local logout even if server logout fails
    }
    logout();
    queryClient.clear();
    void purgeUserScopedCaches();
    navigate('/login');
  };

  return {
    isAuthenticated,
    user,
    signIn,
    signInWithOIDC,
    signOut,
  };
}

export function useOIDCCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        navigate('/login');
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const response = await authService.oidcCallback(
          code,
          state,
          redirectUri
        );

        // Store tokens first so subsequent API calls include Authorization header
        useAuthStore
          .getState()
          .updateTokens(response.access_token, response.refresh_token);

        // Now get user info (this requires the token)
        const userInfo = await authService.getCurrentUser();

        queryClient.clear();
        void purgeUserScopedCaches();

        login(userInfo, response.access_token, response.refresh_token);

        // Redirect to mode selection page
        navigate('/select-mode');
      } catch (_error) {
        console.error('OIDC callback failed:', _error);
        navigate('/login');
      }
    };

    handleCallback();
  }, [searchParams, navigate, login, queryClient]);
}
